export interface PredictionData {
  date: string;
  predicted_volume: number;
  predicted_transactions: number;
  confidence: number;
  trend: 'up' | 'down' | 'stable';
}

export interface FraudAlert {
  transaction_id: string;
  risk_score: number;
  risk_factors: string[];
  recommendation: string;
  timestamp: string;
}

export interface CashFlowPrediction {
  period: string;
  predicted_inflow: number;
  predicted_outflow: number;
  net_flow: number;
  confidence: number;
}

class AIPredictionsService {
  // Simulation de prédictions de volume de transactions
  generateVolumePredictions(days: number = 30): PredictionData[] {
    const predictions: PredictionData[] = [];
    const baseVolume = 1500000; // Volume de base en CFA
    const baseTransactions = 450; // Nombre de transactions de base

    for (let i = 0; i < days; i++) {
      const date = new Date();
      date.setDate(date.getDate() + i);

      // Simulation de tendances avec variations saisonnières
      const seasonalFactor = 1 + 0.2 * Math.sin((i / 30) * 2 * Math.PI);
      const randomFactor = 0.8 + Math.random() * 0.4; // Variation de ±20%
      const weekendFactor = [0, 6].includes(date.getDay()) ? 0.7 : 1; // Réduction weekend

      const predictedVolume = Math.round(baseVolume * seasonalFactor * randomFactor * weekendFactor);
      const predictedTransactions = Math.round(baseTransactions * seasonalFactor * randomFactor * weekendFactor);

      // Calcul de la confiance (plus faible pour les prédictions lointaines)
      const confidence = Math.max(0.6, 0.95 - (i * 0.01));

      // Détermination de la tendance
      let trend: 'up' | 'down' | 'stable' = 'stable';
      if (seasonalFactor > 1.1) trend = 'up';
      else if (seasonalFactor < 0.9) trend = 'down';

      predictions.push({
        date: date.toISOString().split('T')[0],
        predicted_volume: predictedVolume,
        predicted_transactions: predictedTransactions,
        confidence,
        trend
      });
    }

    return predictions;
  }

  // Détection de fraude basée sur des patterns
  detectFraudulentTransactions(): FraudAlert[] {
    const alerts: FraudAlert[] = [];
    
    // Simulation d'alertes de fraude
    const riskFactors = [
      'Montant inhabituel pour ce marchand',
      'Fréquence de transactions élevée',
      'Nouvelle géolocalisation',
      'Pattern de temps suspect',
      'Échecs répétés de PIN',
      'Appareil non reconnu'
    ];

    for (let i = 0; i < 3; i++) {
      const riskScore = 0.3 + Math.random() * 0.7; // Score entre 0.3 et 1.0
      const selectedFactors = riskFactors
        .sort(() => 0.5 - Math.random())
        .slice(0, 2 + Math.floor(Math.random() * 3));

      alerts.push({
        transaction_id: `TXN${String(Math.floor(Math.random() * 999999)).padStart(6, '0')}`,
        risk_score: Math.round(riskScore * 100) / 100,
        risk_factors: selectedFactors,
        recommendation: riskScore > 0.7 ? 'Bloquer la transaction' : 'Surveillance renforcée',
        timestamp: new Date().toISOString()
      });
    }

    return alerts.sort((a, b) => b.risk_score - a.risk_score);
  }

  // Prédictions de cash flow
  generateCashFlowPredictions(): CashFlowPrediction[] {
    const predictions: CashFlowPrediction[] = [];
    const periods = ['Cette semaine', 'Semaine prochaine', 'Ce mois', 'Mois prochain'];

    periods.forEach(period => {
      const baseInflow = 2000000 + Math.random() * 1000000;
      const baseOutflow = 1500000 + Math.random() * 800000;
      
      predictions.push({
        period,
        predicted_inflow: Math.round(baseInflow),
        predicted_outflow: Math.round(baseOutflow),
        net_flow: Math.round(baseInflow - baseOutflow),
        confidence: 0.75 + Math.random() * 0.2
      });
    });

    return predictions;
  }

  // Recommandations intelligentes
  generateRecommendations(): string[] {
    const recommendations = [
      'Augmentez vos limites Orange Money pour capturer plus de volume',
      'Optimisez vos frais Airtel pour améliorer la compétitivité',
      'Considérez l\'ajout de M-Pesa pour diversifier vos canaux',
      'Programmez des rechargements automatiques pour éviter les ruptures',
      'Analysez les pics de 14h-16h pour optimiser vos ressources',
      'Implémentez des alertes de seuil pour une gestion proactive'
    ];

    return recommendations
      .sort(() => 0.5 - Math.random())
      .slice(0, 3);
  }

  // Analyse des patterns de transactions
  analyzeTransactionPatterns() {
    return {
      peak_hours: ['09:00-11:00', '14:00-16:00', '18:00-20:00'],
      peak_days: ['Lundi', 'Mercredi', 'Vendredi'],
      average_transaction_size: 45000,
      most_active_operator: 'Orange',
      growth_rate: '+15.2%',
      seasonal_trends: {
        'Début de mois': '+25%',
        'Fin de mois': '-15%',
        'Weekend': '-30%'
      }
    };
  }
}

export const aiPredictionsService = new AIPredictionsService();